import { Icons } from '@/icons';
import React from 'react';
import { Range } from 'react-range';

const MIN = 1;
const MAX = 100;

interface Props {
  start: string;
  end: string;
  totalDays: number;
  daily: number;
  onStart: (v: string) => void;
  onEnd: (v: string) => void;
  onDaily: (v: number) => void;
}

export const BudgetScheduler: React.FC<Props> = ({
  start,
  end,
  totalDays,
  daily,
  onStart,
  onEnd,
  onDaily,
}) => (
  <details className='group rounded-xl overflow-hidden bg-[#F5F5F5] p-4'>
    {/* Header / Summary */}
    <summary className='flex items-center justify-between bg-[#F5F5F5] text-lg font-medium cursor-pointer'>
      <span className='font-normal text-base'>Budget</span>
      <span className='transform rotate-180 transition-transform duration-200 group-open:rotate-0 mr-4'>
        <Icons.ChivronUp />
      </span>
    </summary>

    {/* Divider */}
    <hr className='mt-5 mb-2 border-t border-gray-400' />

    {/* Content */}
    <div className='bg-[#F5F5F5]'>
      <span className='font-normal text-base'>Schedule</span>
      {/* Date Range */}
      <div className='grid grid-cols-3 gap-2 pt-2'>
        <div className='bg-white py-[5px] px-4 border-b border-gray-300 font-normal text-gray-700 text-sm'>
          Start
          <input
            type='date'
            value={start}
            onChange={(e) => onStart(e.target.value)}
            className='w-full font-normal text-black text-base'
          />
        </div>
        <div className='bg-white py-[5px] px-4 border-b border-gray-300 font-normal text-gray-700 text-sm'>
          End
          <input
            type='date'
            value={end}
            onChange={(e) => onEnd(e.target.value)}
            className='w-full font-normal text-black text-base'
          />
        </div>
        <div className='py-[5px] px-4 flex flex-col font-normal bg-white border-b border-gray-300'>
          <span className='font-normal text-gray-700 text-sm'>Total</span>
          {totalDays}
        </div>
      </div>

      {/* Daily Budget */}

      <div className='mt-[30px] space-y-6'>
        <label className='mb-12 block font-normal text-base'>
          Daily Budget: ${daily}
        </label>

        <div className='flex items-center space-x-4 w-full'>
          {/* Min Label */}
          <span className='text-lg font-normal w-[20px]'>${MIN}</span>

          {/* Slider */}
          <Range
            step={1}
            min={MIN}
            max={MAX}
            values={[daily]}
            onChange={(values) => onDaily(values[0])}
            renderTrack={({ props, children }) => (
              <div
                {...props}
                className='h-4 flex-1 flex items-center'
                style={{ position: 'relative' }}
              >
                {/* Background Track */}
                <div className='h-[14px] w-full rounded-full bg-[#E9E9E9]' />

                {/* Active Track */}
                <div
                  className='absolute h-[14px] rounded-l-[10px] rounded-r-[3px] bg-[#FF0050]'
                  style={{
                    width: `${((daily - MIN) / (MAX - MIN)) * 100 - 2}%`,
                  }}
                />
                {/* dot */}
                <div
                  className='absolute w-[5px] bg-[#FF0050] rounded-lg'
                  style={{
                    left: `${97}%`,
                    top: '+5.5px',
                    bottom: '+5.5px',
                  }}
                />

                <div
                  className='absolute w-[5px] bg-white rounded-lg'
                  style={{
                    left: `${1.3}%`,
                    top: '+5.5px',
                    bottom: '+5.5px',
                  }}
                />

                {children}
              </div>
            )}
            renderThumb={({ props, index }) => {
              const { key, ...rest } = props;
              return (
                <div
                  key={key}
                  {...rest}
                  className='relative flex flex-col items-center mt-6'
                >
                  {/* Value bubble */}
                  <div className='flex justify-center items-center mb-1 h-9 w-10 py-1 text-xs bg-[#322F35] text-white rounded-full'>
                    ${daily}
                  </div>

                  {/* Thumb */}
                  <div className='w-[4px] h-10 rounded-full bg-[#FF0050] cursor-pointer' />
                </div>
              );
            }}
          />

          {/* Max Label */}
          <span className='text-lg font-normal w-[40px]'>${MAX}</span>
        </div>
      </div>
    </div>
  </details>
);
